import { useState } from 'react'
import { ChevronRight, ChevronDown, MoreVertical, Edit, Trash2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ClassificationBadge } from './classification-badge'
import { cn } from '@/lib/utils'
import type { ClassificationNode } from '@/types/classification'

interface ClassificationTreeProps {
  nodes: ClassificationNode[]
  onEdit?: (node: ClassificationNode) => void
  onDelete?: (node: ClassificationNode) => void
  onAddChild?: (parentNode: ClassificationNode) => void
  onSelect?: (node: ClassificationNode) => void
  selectedId?: string
}

/**
 * ClassificationTree displays hierarchical classification taxonomy
 *
 * Features:
 * - Expand/collapse nodes
 * - Select classification
 * - Actions: edit, delete, add child
 * - Color-coded badges
 */
export function ClassificationTree({
  nodes,
  onEdit,
  onDelete,
  onAddChild,
  onSelect,
  selectedId,
}: ClassificationTreeProps) {
  if (nodes.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No classifications found
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {nodes.map((node) => (
        <ClassificationTreeNode
          key={node.id}
          node={node}
          level={0}
          onEdit={onEdit}
          onDelete={onDelete}
          onAddChild={onAddChild}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      ))}
    </div>
  )
}

interface ClassificationTreeNodeProps {
  node: ClassificationNode
  level: number
  onEdit?: (node: ClassificationNode) => void
  onDelete?: (node: ClassificationNode) => void
  onAddChild?: (parentNode: ClassificationNode) => void
  onSelect?: (node: ClassificationNode) => void
  selectedId?: string
}

function ClassificationTreeNode({
  node,
  level,
  onEdit,
  onDelete,
  onAddChild,
  onSelect,
  selectedId,
}: ClassificationTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const hasChildren = node.children && node.children.length > 0
  const isSelected = selectedId === node.id

  const handleToggle = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded)
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    // Don't select if clicking on action buttons
    if ((e.target as HTMLElement).closest('button')) {
      return
    }
    onSelect?.(node)
  }

  return (
    <div className="select-none">
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors',
          isSelected && 'bg-accent',
          onSelect && 'cursor-pointer'
        )}
        style={{ paddingLeft: `${level * 1.5 + 0.75}rem` }}
        onClick={handleClick}
      >
        {/* Expand/collapse button */}
        <button
          type="button"
          className="shrink-0 p-0.5 hover:bg-muted rounded"
          onClick={(e) => {
            e.stopPropagation()
            handleToggle()
          }}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : (
            <div className="h-4 w-4" />
          )}
        </button>

        {/* Badge */}
        <ClassificationBadge
          level={node.level}
          customColor={node.color}
          showTooltip={false}
        />

        {/* Name and description */}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{node.displayName}</div>
          {node.description && (
            <div className="text-xs text-muted-foreground truncate">
              {node.description}
            </div>
          )}
        </div>

        {/* Regulatory frameworks */}
        {node.regulatoryFramework && node.regulatoryFramework.length > 0 && (
          <div className="flex gap-1">
            {node.regulatoryFramework.slice(0, 3).map((framework) => (
              <span
                key={framework}
                className="text-xs px-2 py-0.5 bg-muted rounded"
              >
                {framework}
              </span>
            ))}
            {node.regulatoryFramework.length > 3 && (
              <span className="text-xs px-2 py-0.5 bg-muted rounded">
                +{node.regulatoryFramework.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Actions menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onAddChild && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onAddChild(node)
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Child Classification
              </DropdownMenuItem>
            )}
            {onEdit && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit(node)
                }}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(node)
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Child nodes */}
      {hasChildren && isExpanded && (
        <div className="mt-1">
          {node.children.map((child) => (
            <ClassificationTreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              onSelect={onSelect}
              selectedId={selectedId}
            />
          ))}
        </div>
      )}
    </div>
  )
}
